import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Search, Download, ChevronLeft, ChevronRight, Plus, Bookmark, Check, MapPin, Building2, Hash, Loader2, Settings2, Clock, History, ChevronDown, Database, ExternalLink, FileText, FolderPlus, GripVertical, ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw, Globe, Shield, Zap, AlertTriangle, Droplets, Landmark, Leaf, X, Info, Users, Award, Home, FileBarChart, Crown, FileStack } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';

// City configurations
const CITY_CONFIG = {
  nyc: {
    name: 'New York City',
    color: '#3b82f6',
    datasets: ['dob_violations', 'permits', 'dob_now', 'ecb_violations', 'fire_violations', 'hpd_violations', 'bsa_applications', 'certificate_of_occupancy', 'oer_sites', 'dep_environmental', 'e_designations']
  },
  dc: {
    name: 'Washington DC',
    color: '#ec4899',
    datasets: ['dc_building_permits', 'dc_service_requests', 'dc_cama_residential', 'dc_cama_commercial', 'dc_affordable_housing']
  }
};

// Dataset display names and their available fields
const DATASET_CONFIG = {
  dob_violations: {
    label: 'DOB Violations',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      bin: { label: 'BIN', default: false },
      violation_number: { label: 'Violation #', default: true },
      issue_date: { label: 'Issue Date', default: true },
      status: { label: 'Status', default: true },
      severity: { label: 'Severity', default: true },
      violation_type: { label: 'Violation Type', default: true },
      description: { label: 'Description', default: false },
      disposition_comments: { label: 'Disposition', default: false },
      ecb_number: { label: 'ECB Number', default: false },
      device_number: { label: 'Device #', default: false }
    }
  },
  permits: {
    label: 'DOB BIS Applications',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      bin: { label: 'BIN', default: false },
      permit_id: { label: 'Job #', default: true },
      application_number: { label: 'Application #', default: true },
      job_type: { label: 'Job Type', default: true },
      issue_date: { label: 'Filing Date', default: true },
      status: { label: 'Status', default: true },
      permit_date: { label: 'Permit Date', default: false },
      approved_date: { label: 'Approved', default: false },
      owner: { label: 'Owner', default: false },
      estimated_cost: { label: 'Est. Cost', default: false },
      building_class: { label: 'Bldg Class', default: false },
      zoning: { label: 'Zoning', default: false },
      description: { label: 'Description', default: false }
    }
  },
  dob_now: {
    label: 'DOB NOW Build',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      bin: { label: 'BIN', default: false },
      permit_id: { label: 'Filing #', default: true },
      job_type: { label: 'Job Type', default: true },
      issue_date: { label: 'Filing Date', default: true },
      status: { label: 'Status', default: true },
      permit_date: { label: 'Permit Date', default: false },
      owner: { label: 'Owner', default: false },
      estimated_cost: { label: 'Est. Cost', default: false },
      nycecc_compliance: { label: 'NYCECC', default: false },
      description: { label: 'Description', default: false }
    }
  },
  fire_violations: {
    label: 'Fire Violations',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      bin: { label: 'BIN', default: false },
      issue_date: { label: 'Issue Date', default: true },
      status: { label: 'Status', default: true },
      severity: { label: 'Severity', default: true },
      violation_type: { label: 'Violation Type', default: true },
      description: { label: 'Description', default: false }
    }
  },
  ecb_violations: {
    label: 'ECB Violations',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      bin: { label: 'BIN', default: false },
      violation_id: { label: 'ECB #', default: true },
      issue_date: { label: 'Issue Date', default: true },
      status: { label: 'Status', default: true },
      severity: { label: 'Severity', default: true },
      violation_type: { label: 'Infraction', default: true },
      penalty_amount: { label: 'Penalty', default: false },
      hearing_date: { label: 'Hearing Date', default: false },
      respondent: { label: 'Respondent', default: false },
      description: { label: 'Description', default: false }
    }
  },
  mta: {
    label: 'MTA',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      issue_date: { label: 'Issue Date', default: true },
      status: { label: 'Status', default: true },
      severity: { label: 'Severity', default: true },
      violation_type: { label: 'Violation Type', default: true }
    }
  },
  dot_violations: {
    label: 'DOT Violations',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      issue_date: { label: 'Issue Date', default: true },
      status: { label: 'Status', default: true },
      severity: { label: 'Severity', default: true },
      violation_type: { label: 'Violation Type', default: true }
    }
  },
  oath_hearings: {
    label: 'OATH Hearings',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      issue_date: { label: 'Violation Date', default: true },
      hearing_date: { label: 'Hearing Date', default: true },
      status: { label: 'Status', default: true },
      severity: { label: 'Severity', default: true },
      hearing_result: { label: 'Hearing Result', default: true },
      issuing_agency: { label: 'Issuing Agency', default: true },
      penalty_imposed: { label: 'Penalty Imposed', default: false },
      balance_due: { label: 'Balance Due', default: false },
      description: { label: 'Description', default: false }
    }
  },
  oer_sites: {
    label: 'OER Cleanup Sites',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      borough: { label: 'Borough', default: true },
      description: { label: 'Project Name', default: true },
      violation_type: { label: 'Program', default: true },
      status: { label: 'Class', default: true },
      phase: { label: 'Phase', default: true },
      severity: { label: 'Severity', default: false },
      neighborhood: { label: 'Neighborhood', default: false }
    }
  },
  dep_environmental: {
    label: 'DEP Environmental',
    fields: {
      address: { label: 'Address', default: true },
      borough: { label: 'Borough', default: true },
      zip_code: { label: 'ZIP', default: false },
      violation_type: { label: 'Complaint Type', default: true },
      description: { label: 'Description', default: true },
      status: { label: 'Status', default: true },
      issue_date: { label: 'Date Received', default: true },
      severity: { label: 'Severity', default: false }
    }
  },
  dob_now_electrical: {
    label: 'DOB NOW Electrical',
    fields: {
      address: { label: 'Address', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      bin: { label: 'BIN', default: false },
      permit_id: { label: 'Filing #', default: true },
      job_number: { label: 'Job #', default: true },
      status: { label: 'Filing Status', default: true },
      job_status: { label: 'Job Status', default: false },
      issue_date: { label: 'Filing Date', default: true },
      permit_date: { label: 'Permit Date', default: false },
      work_category: { label: 'Work Category', default: true },
      business_name: { label: 'Business', default: false },
      firm_name: { label: 'Firm', default: false },
      description: { label: 'Description', default: false }
    }
  },
  e_designations: {
    label: 'E-Designations',
    fields: {
      violation_id: { label: 'E-Number', default: true },
      bbl: { label: 'BBL', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      borough: { label: 'Borough', default: true },
      hazmat: { label: 'Hazmat', default: true },
      air_quality: { label: 'Air Quality', default: true },
      noise: { label: 'Noise', default: true },
      description: { label: 'Requirements', default: true },
      issue_date: { label: 'Effective Date', default: true }
    }
  },
  hpd_violations: {
    label: 'HPD Violations',
    fields: {
      address: { label: 'Address', default: true },
      borough: { label: 'Borough', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      apartment: { label: 'Apartment', default: true },
      floor: { label: 'Floor', default: false },
      violation_class: { label: 'Class', default: true },
      description: { label: 'Description', default: true },
      issue_date: { label: 'NOV Issued', default: true },
      status: { label: 'Status', default: true },
      rent_impairing: { label: 'Rent Impairing', default: true }
    }
  },
  bsa_applications: {
    label: 'BSA Applications',
    fields: {
      address: { label: 'Address', default: true },
      borough: { label: 'Borough', default: true },
      block: { label: 'Block', default: true },
      lot: { label: 'Lot', default: true },
      application_type: { label: 'Application Type', default: true },
      description: { label: 'Project Description', default: true },
      issue_date: { label: 'Filed Date', default: true },
      status: { label: 'Status', default: true },
      zoning_district: { label: 'Zoning District', default: true },
      community_board: { label: 'Community Board', default: false },
      decisions_url: { label: 'Decision Link', default: true }
    }
  },
  // DC Datasets
  dc_building_permits: {
    label: 'DC Building Permits',
    city: 'dc',
    fields: {
      address: { label: 'Address', default: true },
      permit_id: { label: 'Permit ID', default: true },
      permit_type: { label: 'Permit Type', default: true },
      category: { label: 'Category', default: true },
      status: { label: 'Status', default: true },
      description: { label: 'Description', default: true },
      issue_date: { label: 'Issue Date', default: true },
      ward: { label: 'Ward', default: true },
      owner_name: { label: 'Owner', default: false },
      applicant: { label: 'Applicant', default: false }
    }
  },
  dc_service_requests: {
    label: 'DC 311 Requests',
    city: 'dc',
    fields: {
      address: { label: 'Address', default: true },
      request_id: { label: 'Request ID', default: true },
      description: { label: 'Service Type', default: true },
      service_type: { label: 'Category', default: true },
      agency: { label: 'Agency', default: true },
      status: { label: 'Status', default: true },
      issue_date: { label: 'Date Added', default: true },
      ward: { label: 'Ward', default: true },
      priority: { label: 'Priority', default: false }
    }
  },
  dc_cama_residential: {
    label: 'DC Residential Properties',
    city: 'dc',
    fields: {
      address: { label: 'Address', default: true },
      ssl: { label: 'SSL', default: true },
      use_code: { label: 'Use Code', default: true },
      bedrooms: { label: 'Bedrooms', default: true },
      bathrooms: { label: 'Bathrooms', default: true },
      stories: { label: 'Stories', default: true },
      sale_price: { label: 'Sale Price', default: true },
      sale_date: { label: 'Sale Date', default: true }
    }
  },
  dc_cama_commercial: {
    label: 'DC Commercial Properties',
    city: 'dc',
    fields: {
      address: { label: 'Address', default: true },
      ssl: { label: 'SSL', default: true },
      use_code: { label: 'Use Code', default: true },
      stories: { label: 'Stories', default: true },
      gross_building_area: { label: 'GBA', default: true },
      sale_price: { label: 'Sale Price', default: true },
      sale_date: { label: 'Sale Date', default: true }
    }
  },
  dc_affordable_housing: {
    label: 'DC Affordable Housing',
    city: 'dc',
    fields: {
      address: { label: 'Address', default: true },
      project_name: { label: 'Project Name', default: true },
      status: { label: 'Status', default: true },
      affordable_units: { label: 'Affordable Units', default: true },
      agency: { label: 'Agency', default: true },
      ward: { label: 'Ward', default: true }
    }
  }
};

// Search modes
const SEARCH_MODES = [
  { id: 'address', label: 'Address', icon: MapPin },
  { id: 'blocklot', label: 'Block/Lot', icon: Building2 },
  { id: 'bin', label: 'BIN', icon: Hash },
  { id: 'application', label: 'Application #', icon: FileText }
];

// All available columns with their config
const ALL_COLUMNS = [
  { id: '_dataset', label: 'Dataset', default: true },
  { id: 'address', label: 'Address', default: true },
  { id: 'block', label: 'Block', default: true },
  { id: 'lot', label: 'Lot', default: true },
  { id: 'bin', label: 'BIN', default: false },
  { id: 'violation_number', label: 'Violation #', default: true },
  { id: 'application_number', label: 'Application #', default: true },
  { id: 'permit_id', label: 'Permit/Job #', default: true },
  { id: 'ecb_number', label: 'ECB #', default: false },
  { id: 'violation_type', label: 'Type', default: true },
  { id: 'application_type', label: 'App Type', default: false },
  { id: 'issue_date', label: 'Issue Date', default: true },
  { id: 'status', label: 'Status', default: true },
  { id: 'severity', label: 'Severity', default: true },
  { id: 'zoning_district', label: 'Zoning', default: false },
  { id: 'description', label: 'Description', default: false },
  { id: 'penality_amount', label: 'Penalty', default: false },
  { id: 'decisions_url', label: 'Decision', default: false },
  { id: 'hearing_date', label: 'Hearing Date', default: false },
  { id: 'hearing_result', label: 'Hearing Result', default: false },
  { id: 'issuing_agency', label: 'Issuing Agency', default: false },
  { id: 'penalty_imposed', label: 'Penalty Imposed', default: false },
  { id: 'balance_due', label: 'Balance Due', default: false },
  { id: 'apartment', label: 'Apartment', default: false },
  { id: 'violation_class', label: 'Class', default: false },
  { id: 'rent_impairing', label: 'Rent Impairing', default: false },
  { id: 'community_board', label: 'Community Board', default: false },
  { id: 'owner', label: 'Owner', default: false },
  { id: 'estimated_cost', label: 'Est. Cost', default: false },
  { id: 'zoning', label: 'Zoning', default: false },
  { id: 'phase', label: 'Phase', default: false },
  { id: 'neighborhood', label: 'Neighborhood', default: false }
];

// Default column order (just the IDs)
const DEFAULT_COLUMN_ORDER = ALL_COLUMNS.filter(c => c.default).map(c => c.id);

export default function CompliancePage() {
  const { user, api } = useAuth();
  const [searchParams] = useSearchParams();
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 20;

  // Search usage tracking
  const [searchUsage, setSearchUsage] = useState(null);

  // API connectivity error state
  const [apiErrorDialog, setApiErrorDialog] = useState({ open: false, message: '', details: '' });

  // City selection state
  const [selectedCity, setSelectedCity] = useState(() => {
    const saved = localStorage.getItem('insite360_selected_city');
    return saved || 'nyc';
  });

  // Search mode state
  const [searchMode, setSearchMode] = useState('address');
  
  // Get URL params for pre-filtering from dashboard
  const urlStatus = searchParams.get('status');
  const urlSeverity = searchParams.get('severity');

  // Available violation datasets based on selected city
  const violationDatasets = useMemo(() => {
    const cityDatasets = CITY_CONFIG[selectedCity]?.datasets || [];
    return cityDatasets.filter(ds => DATASET_CONFIG[ds]);
  }, [selectedCity]);

  // Handle city change
  const handleCityChange = (city) => {
    setSelectedCity(city);
    localStorage.setItem('insite360_selected_city', city);
    // Reset to first dataset of new city
    const firstDataset = CITY_CONFIG[city]?.datasets?.[0] || 'dob_violations';
    setFilters(prev => ({ ...prev, datasets: [firstDataset] }));
    setViolations([]);
    setTotal(0);
  };

  // Get initial dataset based on saved city
  const getInitialDataset = () => {
    const savedCity = localStorage.getItem('insite360_selected_city') || 'nyc';
    return CITY_CONFIG[savedCity]?.datasets?.[0] || 'dob_violations';
  };

  // Filters state
  const [filters, setFilters] = useState({
    datasets: [getInitialDataset()],  // Now an array for multi-select
    address: '',
    block: '',
    lot: '',
    bin: '',
    applicationId: '',  // Application number search
    borough: 'all',
    status: urlStatus || 'all',
    severity: urlSeverity || 'all'
  });

  // Visible columns state - initialized from all dataset configs (union of all fields)
  // Version key to reset cached columns when ALL_COLUMNS changes
  const COLUMN_VERSION = 'v2';
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const savedVersion = localStorage.getItem('insite360_column_version');
      if (savedVersion === COLUMN_VERSION) {
        const saved = localStorage.getItem('insite360_visible_columns');
        if (saved) return JSON.parse(saved);
      }
    } catch { /* use defaults */ }
    const defaultCols = {};
    ALL_COLUMNS.forEach(col => {
      defaultCols[col.id] = col.default;
    });
    localStorage.setItem('insite360_column_version', COLUMN_VERSION);
    return defaultCols;
  });

  // Column order state - controls the order columns appear
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const savedVersion = localStorage.getItem('insite360_column_version');
      if (savedVersion === COLUMN_VERSION) {
        const saved = localStorage.getItem('insite360_column_order');
        if (saved) return JSON.parse(saved);
      }
    } catch { /* use defaults */ }
    return [...DEFAULT_COLUMN_ORDER];
  });
  
  // Column settings dialog
  const [showColumnSettings, setShowColumnSettings] = useState(false);

  // Recent searches state - stored in localStorage
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const saved = localStorage.getItem('insite360_recent_searches');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Saved searches state - explicitly saved by user
  const [savedSearches, setSavedSearches] = useState(() => {
    try {
      const saved = localStorage.getItem('insite360_saved_searches');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Save search dialog
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');

  // Fetch search usage on mount
  useEffect(() => {
    const fetchSearchUsage = async () => {
      try {
        const res = await api().get('/tier/info');
        setSearchUsage(res.data.limits);
      } catch { /* ignore */ }
    };
    fetchSearchUsage();
  }, [api]);

  // Save a search to recent searches (auto)
  const saveRecentSearch = (searchParams) => {
    const newSearch = {
      id: Date.now(),
      ...searchParams,
      date: new Date().toISOString()
    };
    
    // Keep only last 3 unique searches
    const updated = [newSearch, ...recentSearches.filter(s => 
      !(s.searchMode === searchParams.searchMode && 
        JSON.stringify(s.datasets) === JSON.stringify(searchParams.datasets) &&
        s.address === searchParams.address &&
        s.block === searchParams.block &&
        s.lot === searchParams.lot &&
        s.bin === searchParams.bin)
    )].slice(0, 3);
    
    setRecentSearches(updated);
    localStorage.setItem('insite360_recent_searches', JSON.stringify(updated));
  };

  // Explicitly save a search with a name
  const saveSearchWithName = () => {
    if (!saveSearchName.trim()) {
      toast.error('Please enter a name for this search');
      return;
    }
    
    const newSavedSearch = {
      id: Date.now(),
      name: saveSearchName.trim(),
      searchMode,
      datasets: filters.datasets,
      address: filters.address,
      block: filters.block,
      lot: filters.lot,
      bin: filters.bin,
      borough: filters.borough,
      date: new Date().toISOString()
    };
    
    const updated = [newSavedSearch, ...savedSearches].slice(0, 10); // Keep max 10 saved searches
    setSavedSearches(updated);
    localStorage.setItem('insite360_saved_searches', JSON.stringify(updated));
    
    toast.success(`Search "${saveSearchName}" saved`);
    setShowSaveDialog(false);
    setSaveSearchName('');
  };

  // Delete a saved search
  const deleteSavedSearch = (searchId) => {
    const updated = savedSearches.filter(s => s.id !== searchId);
    setSavedSearches(updated);
    localStorage.setItem('insite360_saved_searches', JSON.stringify(updated));
    toast.success('Search deleted');
  };

  // Load a recent/saved search
  const loadRecentSearch = (search) => {
    setSearchMode(search.searchMode || 'address');
    // Handle both old format (dataset) and new format (datasets)
    const datasets = search.datasets || (search.dataset ? [search.dataset] : ['dob_violations']);
    setFilters(prev => ({
      ...prev,
      datasets: datasets,
      address: search.address || '',
      block: search.block || '',
      lot: search.lot || '',
      bin: search.bin || '',
      borough: search.borough || 'all'
    }));
    
    // Auto-execute the search after state updates
    setTimeout(() => {
      executeSearchWithParams({ ...search, datasets });
    }, 100);
  };

  // Execute search with specific params (for loading saved searches)
  const executeSearchWithParams = async (search) => {
    setLoading(true);
    try {
      const datasets = search.datasets || (search.dataset ? [search.dataset] : ['dob_violations']);
      const params = new URLSearchParams({
        datasets: datasets.join(','),
        limit: '20',
        offset: '0'
      });
      
      if (search.searchMode === 'address' && search.address) {
        params.append('address', search.address);
      }
      if ((search.searchMode === 'blocklot' || search.block) && search.block) {
        params.append('block', search.block);
      }
      if ((search.searchMode === 'blocklot' || search.lot) && search.lot) {
        params.append('lot', search.lot);
      }
      if (search.searchMode === 'bin' && search.bin) {
        params.append('bin', search.bin);
      }
      if (search.borough && search.borough !== 'all') {
        params.append('borough', search.borough);
      }
      // DC-specific params
      if (search.ward) {
        params.append('ward', search.ward);
      }
      if (search.ssl) {
        params.append('ssl', search.ssl);
      }

      // Use different API endpoint based on city
      const endpoint = selectedCity === 'dc' ? '/dc/search' : '/violations/search';
      const res = await api().get(`${endpoint}?${params}`);
      setViolations(res.data.data || []);
      setTotal(res.data.total || 0);
      setPage(0);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Clear form for new search
  const startNewSearch = () => {
    setSearchMode('address');
    setFilters(prev => ({
      ...prev,
      address: '',
      block: '',
      lot: '',
      bin: '',
      status: 'all',
      severity: 'all'
    }));
    setViolations([]);
    setTotal(0);
  };

  // Format relative date
  const formatRelativeDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get search display label
  const getSearchLabel = (search) => {
    if (search.searchMode === 'address' && search.address) {
      return search.address.length > 30 ? search.address.substring(0, 30) + '...' : search.address;
    }
    if (search.searchMode === 'blocklot' && search.block && search.lot) {
      return `Block ${search.block}, Lot ${search.lot}`;
    }
    if (search.searchMode === 'bin' && search.bin) {
      return `BIN ${search.bin}`;
    }
    return 'Search';
  };

  // Address autocomplete state
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const addressInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Watchlist state
  const [watchlists, setWatchlists] = useState([]);
  const [addToWatchlistDialog, setAddToWatchlistDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [selectedWatchlistId, setSelectedWatchlistId] = useState('');
  
  // Quick create watchlist from results
  const [showCreateWatchlistDialog, setShowCreateWatchlistDialog] = useState(false);
  const [quickWatchlistName, setQuickWatchlistName] = useState('');
  const [selectedResults, setSelectedResults] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Risk Score feature - removed per-row display, now only in Property Card
  const [showRiskScores, setShowRiskScores] = useState(false);
  const [riskScores, setRiskScores] = useState({});
  const [loadingRiskScores, setLoadingRiskScores] = useState(false);

  // Property Card Modal
  const [propertyCardOpen, setPropertyCardOpen] = useState(false);
  const [propertyCardData, setPropertyCardData] = useState(null);
  const [propertyCardLoading, setPropertyCardLoading] = useState(false);
  const [generatingEnhancedReport, setGeneratingEnhancedReport] = useState(false);
  
  // Check if user has enterprise tier
  const isEnterpriseTier = user?.tier?.includes('enterprise') || user?.role === 'admin';

  // Update visible columns when datasets change - show dataset column only for multi-select
  useEffect(() => {
    setVisibleColumns(prev => ({
      ...prev,
      _dataset: filters.datasets.length > 1  // Show dataset column when multiple datasets selected
    }));
  }, [filters.datasets]);

  // Track previous datasets to detect real changes
  const prevDatasetsRef = useRef(filters.datasets);
  const initialMountRef = useRef(true);
  
  // Auto-refresh results when datasets change
  useEffect(() => {
    // Skip initial mount
    if (initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }
    
    // Compare arrays properly
    const datasetsChanged = JSON.stringify(prevDatasetsRef.current) !== JSON.stringify(filters.datasets);
    
    if (!datasetsChanged) {
      return;
    }
    
    // Update ref for next comparison
    prevDatasetsRef.current = filters.datasets;
    
    console.log('[Auto-refresh] Datasets changed:', filters.datasets);
    
    // Check if there's active search criteria
    const hasSearchCriteria = filters.address || filters.block || filters.lot || filters.bin;
    
    // Refresh if we have search criteria and at least one dataset
    if (hasSearchCriteria && filters.datasets.length > 0) {
      console.log('[Auto-refresh] Triggering fetch with search criteria');
      setPage(0);
      fetchViolations();
    } else if (hasSearchCriteria && filters.datasets.length === 0) {
      // Clear results if no datasets selected
      console.log('[Auto-refresh] Clearing results - no datasets selected');
      setViolations([]);
      setTotal(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.datasets]);

  // Track previous status/severity to detect real changes
  const prevStatusRef = useRef(filters.status);
  const prevSeverityRef = useRef(filters.severity);
  
  // NOTE: The status/severity filter effect is defined AFTER fetchViolations

  const fetchWatchlists = useCallback(async () => {
    try {
      const res = await api().get('/watchlist');
      setWatchlists(res.data);
    } catch (err) {
      console.error('Failed to fetch watchlists:', err);
    }
  }, [api]);

  useEffect(() => {
    fetchWatchlists();
  }, [fetchWatchlists]);

  // Live address lookup using NYC GeoSearch API
  const fetchAddressSuggestions = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      let results = [];
      
      if (selectedCity === 'dc') {
        // Use DC MAR API for DC address search
        const response = await api().get(`/dc/address/search?query=${encodeURIComponent(query)}`);
        if (response.data?.suggestions) {
          results = response.data.suggestions.map(s => ({
            address: s.address,
            full_label: s.full_label,
            ssl: s.ssl,
            ward: s.ward,
            block: '', // DC doesn't use block/lot
            lot: '',
            bin: '',
            city: 'dc'
          }));
        }
      } else {
        // Use NYC ZOLA/GeoSearch for NYC
        const response = await fetch(
          `https://geosearch.planninglabs.nyc/v2/autocomplete?text=${encodeURIComponent(query)}`
        );
        const data = await response.json();
        
        if (data.features) {
          results = data.features.map(f => ({
            address: f.properties.label,
            borough: f.properties.borough || '',
            block: f.properties.pad_bbl ? f.properties.pad_bbl.substring(1, 6).replace(/^0+/, '') : '',
            lot: f.properties.pad_bbl ? f.properties.pad_bbl.substring(6).replace(/^0+/, '') : '',
            bin: f.properties.pad_bin || '',
            city: 'nyc'
          }));
        }
      }
      
      setAddressSuggestions(results);
      setShowSuggestions(results.length > 0);
    } catch (err) {
      console.error('Address lookup failed:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Debounced address search
  const handleAddressChange = (value) => {
    setFilters(prev => ({ ...prev, address: value }));
    
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 300);
  };

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          addressInputRef.current && !addressInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate risk scores for visible results
  const calculateRiskScores = useCallback(async () => {
    if (!violations.length) return;
    
    setLoadingRiskScores(true);
    const newScores = {};
    
    // Group violations by property (block-lot)
    const properties = {};
    violations.forEach(v => {
      const key = `${v.block || v.ssl || ''}-${v.lot || ''}`;
      if (!properties[key]) {
        properties[key] = {
          block: v.block || v.ssl || '',
          lot: v.lot || '',
          address: v.address || v.full_address || '',
          violations: []
        };
      }
      properties[key].violations.push(v);
    });
    
    // Calculate scores for each unique property
    for (const [key, prop] of Object.entries(properties)) {
      try {
        const res = await api().post('/property/risk-score', {
          block: prop.block,
          lot: prop.lot,
          address: prop.address,
          include_ai_analysis: false // Skip AI for speed
        });
        
        if (res.data) {
          newScores[key] = {
            score: res.data.score || 0,
            level: res.data.risk_level || 'LOW',
            factors: res.data.factors || []
          };
        }
      } catch (err) {
        console.error(`Failed to calculate risk for ${key}:`, err);
        // Calculate a basic score from visible violations
        const openViolations = prop.violations.filter(v => 
          v.status?.toLowerCase() === 'open' || v.status?.toLowerCase() === 'active'
        ).length;
        const highSeverity = prop.violations.filter(v => 
          v.severity?.toLowerCase() === 'high' || v.violation_class === 'I'
        ).length;
        
        let score = Math.min(100, openViolations * 15 + highSeverity * 25);
        let level = score < 30 ? 'LOW' : score < 60 ? 'MODERATE' : 'HIGH';
        
        newScores[key] = { score, level, factors: ['Based on visible violations'] };
      }
    }
    
    setRiskScores(newScores);
    setLoadingRiskScores(false);
  }, [violations, api]);

  // Calculate risk scores when toggle is enabled
  useEffect(() => {
    if (showRiskScores && violations.length > 0 && Object.keys(riskScores).length === 0) {
      calculateRiskScores();
    }
  }, [showRiskScores, violations, riskScores, calculateRiskScores]);

  // Clear risk scores when results change
  useEffect(() => {
    setRiskScores({});
  }, [violations]);

  // Get risk score for a violation
  const getRiskScore = (violation) => {
    const key = `${violation.block || violation.ssl || ''}-${violation.lot || ''}`;
    return riskScores[key] || null;
  };

  // Open Property Card Modal
  const openPropertyCard = async (violation) => {
    setPropertyCardOpen(true);
    setPropertyCardLoading(true);
    setPropertyCardData(null);
    
    const block = violation.block || '';
    const lot = violation.lot || '';
    const ssl = violation.ssl || '';
    const address = violation.address || violation.full_address || '';
    const bin = violation.bin || '';
    
    // Check if this is DC data (has SSL) or NYC data (has block/lot)
    const isDCProperty = selectedCity === 'dc' || (ssl && !block);
    
    try {
      if (isDCProperty) {
        // DC Property Card - use DC-specific APIs
        const [dcPropertyRes] = await Promise.allSettled([
          api().get(`/dc/property?ssl=${encodeURIComponent(ssl)}&address=${encodeURIComponent(address)}`)
        ]);
        
        const dcData = dcPropertyRes.status === 'fulfilled' ? dcPropertyRes.value.data : null;
        
        // Count violations from current search results for this property
        const propertyViolations = violations.filter(v => 
          v.ssl === ssl || v.address === address
        );
        const openViolations = propertyViolations.filter(v => 
          v.status?.toLowerCase() === 'open' || v.status?.toLowerCase() === 'active'
        ).length;
        const closedViolations = propertyViolations.filter(v => 
          v.status?.toLowerCase() === 'closed' || v.status?.toLowerCase() === 'resolved'
        ).length;
        
        setPropertyCardData({
          address,
          ssl,
          block: ssl ? ssl.split(' ')[0] : 'N/A', // Extract square from SSL
          lot: ssl ? ssl.split(' ').slice(1).join(' ') : 'N/A', // Extract lot from SSL
          bin: 'N/A', // DC doesn't use BIN
          isDC: true,
          // DC property info
          dcProperty: dcData,
          // Owner info from DC data
          pluto: dcData ? {
            ownerName: dcData.owner_name || 'Unknown',
            assessedValue: dcData.assessed_value ? `$${Number(dcData.assessed_value).toLocaleString()}` : null,
            landArea: dcData.land_area,
            buildingType: dcData.use_code || dcData.property_type,
          } : null,
          // Counts
          totalViolations: propertyViolations.length,
          openViolations,
          closedViolations,
          openApplications: 0,
        });
      } else {
        // NYC Property Card - use NYC-specific APIs (PLUTO, ACRIS, etc.)
        const [riskRes, plutoRes, floodRes, landmarkRes, oerRes, violationCountRes, permitRes, acrisRes, coRes] = await Promise.allSettled([
          // Risk Score
          api().post('/property/risk-score', { block, lot, address, include_ai_analysis: false }),
          // PLUTO data (building info)
          api().get(`/property/pluto?block=${block}&lot=${lot}`),
          // Flood zone
          api().get(`/property/flood-zone?block=${block}&lot=${lot}`),
          // Landmark status
          api().get(`/property/landmark?block=${block}&lot=${lot}`),
          // OER environmental status
          api().get(`/property/oer?block=${block}&lot=${lot}`),
          // Violation counts by status
          api().get(`/violations/search?datasets=dob_violations&block=${block}&lot=${lot}&limit=1`),
          // Open permits/applications
          api().get(`/violations/search?datasets=permits&block=${block}&lot=${lot}&status=open&limit=1`),
          // ACRIS owner info
          api().get(`/property/acris-owner?block=${block}&lot=${lot}`),
          // Certificates of Occupancy
          api().get(`/property/certificates-of-occupancy?block=${block}&lot=${lot}`)
        ]);
        
        // Count violations from current search results for this property
        const propertyViolations = violations.filter(v => 
          (v.block === block || v.block === block.padStart(5, '0')) &&
          (v.lot === lot || v.lot === lot.padStart(5, '0'))
        );
        const openViolations = propertyViolations.filter(v => 
          v.status?.toLowerCase() === 'open' || v.status?.toLowerCase() === 'active'
        ).length;
        const closedViolations = propertyViolations.filter(v => 
          v.status?.toLowerCase() === 'closed' || v.status?.toLowerCase() === 'resolved'
        ).length;

        // Combine owner info from PLUTO and ACRIS
        const plutoData = plutoRes.status === 'fulfilled' ? plutoRes.value.data : null;
        const acrisData = acrisRes.status === 'fulfilled' ? acrisRes.value.data : null;
        
        const ownerSummary = {
          primaryOwner: acrisData?.current_owners?.[0] || plutoData?.owner_name || 'Unknown',
          allCurrentOwners: acrisData?.current_owners || [],
          previousOwners: acrisData?.previous_owners || [],
          plutoOwner: plutoData?.owner_name || null,
          lastTransferDate: acrisData?.document_date || null,
          lastSalePrice: acrisData?.document_amount || null,
          documentType: acrisData?.document_type || null,
          hasAcrisData: acrisData?.current_owners?.length > 0,
          hasPlutoData: !!plutoData?.owner_name
        };
        
        setPropertyCardData({
          address,
          block,
          lot,
          bin: bin || plutoData?.bin || 'N/A',
          isDC: false,
          riskScore: riskRes.status === 'fulfilled' ? riskRes.value.data : null,
          pluto: plutoData,
          floodZone: floodRes.status === 'fulfilled' ? floodRes.value.data : null,
          landmark: landmarkRes.status === 'fulfilled' ? landmarkRes.value.data : null,
          oer: oerRes.status === 'fulfilled' ? oerRes.value.data : null,
          ownerSummary,
          certificatesOfOccupancy: coRes.status === 'fulfilled' ? coRes.value.data : null,
          totalViolations: violationCountRes.status === 'fulfilled' ? violationCountRes.value.data?.total || 0 : propertyViolations.length,
          openViolations,
          closedViolations,
          openApplications: permitRes.status === 'fulfilled' ? permitRes.value.data?.total || 0 : 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch property card data:', err);
      toast.error('Failed to load property details');
    } finally {
      setPropertyCardLoading(false);
    }
  };

  // Generate Enhanced PDF Report (Enterprise Only) - Supports 3 report types
  const handleGenerateEnhancedReport = async (reportType = 'executive') => {
    if (!isEnterpriseTier) {
      toast.error('Enhanced reports require Enterprise tier');
      return;
    }
    
    if (!propertyCardData?.address) {
      toast.error('Unable to generate report - Address not found for this property');
      return;
    }
    
    setGeneratingEnhancedReport(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        address: propertyCardData.address,
        borough: propertyCardData.borough || filters.borough || 'Manhattan',
        report_type: reportType
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/export/enhanced-report-by-address?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate enhanced report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeAddress = propertyCardData.address.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
      const reportLabels = { executive: 'Executive', detailed: 'Detailed', full: 'Full' };
      link.setAttribute('download', `Compliance_${reportLabels[reportType]}_${safeAddress}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${reportLabels[reportType]} report generated!`);
    } catch (err) {
      console.error('Failed to generate enhanced report:', err);
      toast.error(err.message || 'Failed to generate enhanced report');
    } finally {
      setGeneratingEnhancedReport(false);
    }
  };

  const selectAddress = (suggestion) => {
    // When selecting from autocomplete:
    // 1. Set the address string
    // 2. SET THE BOROUGH from the suggestion (critical for correct results)
    // 3. Clear block/lot to prevent them from being sent to backend
    setFilters(prev => ({
      ...prev,
      address: suggestion.address,
      borough: suggestion.borough || prev.borough, // SET BOROUGH from autocomplete selection
      block: '', // Clear block to prevent it from being sent in address mode
      lot: '',   // Clear lot to prevent it from being sent in address mode
      // Store block/lot for property card display only (with underscore prefix)
      _selectedBlock: suggestion.block || '',
      _selectedLot: suggestion.lot || '',
      _selectedBin: suggestion.bin || '',
      ssl: suggestion.ssl || '' // DC uses SSL instead of block/lot
    }));
    setShowSuggestions(false);
  };

  // Execute search
  const handleSearch = async () => {
    // Validate search has criteria
    if (searchMode === 'address' && !filters.address) {
      toast.error('Please enter an address to search');
      return;
    }
    if (searchMode === 'blocklot' && (!filters.block || !filters.lot)) {
      toast.error('Please enter both Block and Lot');
      return;
    }
    if (searchMode === 'bin' && !filters.bin) {
      toast.error('Please enter a BIN number');
      return;
    }
    if (searchMode === 'application' && !filters.applicationId) {
      toast.error('Please enter an application or job number');
      return;
    }
    // Skip dataset check for application search (uses predefined datasets)
    if (searchMode !== 'application' && filters.datasets.length === 0) {
      toast.error('Please select at least one dataset');
      return;
    }
    
    // Save this search to recent searches
    saveRecentSearch({
      searchMode,
      datasets: searchMode === 'application' ? ['permits', 'dob_now'] : filters.datasets,
      address: filters.address,
      block: filters.block,
      lot: filters.lot,
      bin: filters.bin,
      applicationId: filters.applicationId,
      borough: filters.borough
    });
    
    setPage(0);
    fetchViolations();
  };

  const fetchViolations = useCallback(async () => {
    setLoading(true);
    try {
      const hasClientFilter = (filters.status && filters.status !== 'all') || (filters.severity && filters.severity !== 'all');
      const fetchLimit = hasClientFilter ? 500 : limit;
      
      let allData = [];
      let totalCount = 0;
      
      // For application search, always use permits and dob_now datasets
      const searchDatasets = searchMode === 'application' 
        ? ['permits', 'dob_now'] 
        : filters.datasets;
      
      // Build query params based on search mode
      const params = new URLSearchParams({
        datasets: searchDatasets.join(','),
        limit: fetchLimit.toString(),
        offset: hasClientFilter ? '0' : (page * limit).toString()
      });
      
      // Add search criteria based on mode
      // IMPORTANT: Only send the parameters relevant to the current search mode
      // This prevents block/lot from overriding address searches
      if (searchMode === 'address' && filters.address) {
        params.append('address', filters.address);
        // Do NOT send block/lot when searching by address - this was causing incorrect results
      }
      if (searchMode === 'blocklot' && filters.block) {
        params.append('block', filters.block);
      }
      if (searchMode === 'blocklot' && filters.lot) {
        params.append('lot', filters.lot);
      }
      if (searchMode === 'bin' && filters.bin) {
        params.append('bin', filters.bin);
      }
      if (searchMode === 'application' && filters.applicationId) {
        params.append('app_id', filters.applicationId);
      }
      if (filters.borough && filters.borough !== 'all') {
        params.append('borough', filters.borough);
      }
      // DC-specific params
      if (filters.ward) {
        params.append('ward', filters.ward);
      }
      if (filters.ssl) {
        params.append('ssl', filters.ssl);
      }

      // Use different API endpoint based on city
      const endpoint = selectedCity === 'dc' ? '/dc/search' : '/violations/search';
      const res = await api().get(`${endpoint}?${params}`);
      allData = res.data.data || [];
      totalCount = res.data.total || allData.length;
      
      // Client-side filtering for status and severity
      if (filters.status && filters.status !== 'all') {
        const statusFilter = filters.status.toLowerCase().replace('_', ' ');
        allData = allData.filter(v => {
          const vStatus = (v.status || '').toLowerCase();
          return vStatus.includes(statusFilter) || statusFilter.includes(vStatus);
        });
      }
      
      if (filters.severity && filters.severity !== 'all') {
        const sevFilter = filters.severity.toLowerCase();
        allData = allData.filter(v => (v.severity || '').toLowerCase() === sevFilter);
      }
      
      // Apply pagination client-side when using filters
      if (hasClientFilter) {
        const startIdx = page * limit;
        const paginatedData = allData.slice(startIdx, startIdx + limit);
        setViolations(paginatedData);
        setTotal(allData.length);
      } else {
        setViolations(allData);
        setTotal(totalCount);
      }
      
      // Check if results came from error/fallback source
      const errorSources = ['error', 'timeout', 'api_error', 'connection_error'];
      if (errorSources.includes(res.data.source)) {
        const cityName = selectedCity === 'dc' ? 'DC Open Data' : 'NYC Open Data';
        setApiErrorDialog({
          open: true,
          message: `${cityName} Connection Issue`,
          details: `We're experiencing connectivity issues with ${cityName} servers. Some results may be incomplete or unavailable. This is typically a temporary issue with the external data source.`
        });
      }
      
      // Update search usage counter
      setSearchUsage(prev => prev && !prev.searches_unlimited ? {
        ...prev,
        searches_used_today: (prev.searches_used_today || 0) + 1
      } : prev);
    } catch (err) {
      console.error('Failed to fetch violations:', err);
      
      // Check for rate limit / tier restriction errors
      if (err.response?.status === 429) {
        const detail = err.response?.data?.detail;
        toast.error(detail?.message || 'Daily search limit reached. Upgrade your plan for more searches.');
        setLoading(false);
        return;
      }
      if (err.response?.status === 403 && err.response?.data?.detail?.error === 'upgrade_required') {
        toast.error(err.response.data.detail.message || 'Upgrade required for this feature.');
        setLoading(false);
        return;
      }
      
      // Check for specific error types
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
      const isTimeout = (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('timeout')) || err.code === 'ECONNABORTED';
      const isConnectionError = (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('connection')) || err.code === 'ERR_NETWORK';
      
      if (isTimeout || isConnectionError) {
        const cityName = selectedCity === 'dc' ? 'DC Open Data' : 'NYC Open Data';
        setApiErrorDialog({
          open: true,
          message: `${cityName} Connection Timeout`,
          details: `The ${cityName} servers are taking too long to respond. This is usually a temporary issue on their end. Please try again in a few minutes, or try searching with fewer datasets selected.`
        });
      } else {
        toast.error('Failed to fetch violations');
      }
    } finally {
      setLoading(false);
    }
  }, [api, filters, page, searchMode, selectedCity]);

  // Don't auto-fetch on mount - wait for user to search
  // useEffect(() => {
  //   fetchViolations();
  // }, [fetchViolations]);

  // Auto-refresh results when status or severity filters change
  // This effect is placed AFTER fetchViolations is defined to avoid stale closure
  useEffect(() => {
    const statusChanged = prevStatusRef.current !== filters.status;
    const severityChanged = prevSeverityRef.current !== filters.severity;
    
    if (!statusChanged && !severityChanged) {
      return;
    }
    
    // Update refs for next comparison
    prevStatusRef.current = filters.status;
    prevSeverityRef.current = filters.severity;
    
    console.log('[Filter-refresh] Status/Severity changed:', { status: filters.status, severity: filters.severity });
    
    // Only refetch if we have existing results (means there was a search)
    if (violations.length > 0 || total > 0) {
      console.log('[Filter-refresh] Triggering refetch with new filters');
      setPage(0);
      fetchViolations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.severity, fetchViolations]);

  const totalPages = Math.ceil(total / limit);

  // Open dialog to add property to watchlist
  const openAddToWatchlist = (violation) => {
    setSelectedProperty({
      address: violation.address,
      block: violation.block,
      lot: violation.lot,
      datasets: violation._dataset ? [violation._dataset] : filters.datasets // Use record's dataset or current selection
    });
    setAddToWatchlistDialog(true);
    setNewWatchlistName('');
    setSelectedWatchlistId('');
  };

  // Add property to existing watchlist
  const addToExistingWatchlist = async () => {
    if (!selectedWatchlistId || !selectedProperty) return;
    
    const watchlist = watchlists.find(w => w.id === selectedWatchlistId);
    if (!watchlist) return;

    // Check if property already exists
    const exists = watchlist.properties?.some(
      p => p.address === selectedProperty.address && p.block === selectedProperty.block && p.lot === selectedProperty.lot
    );
    if (exists) {
      toast.info('Property already in this watchlist');
      setAddToWatchlistDialog(false);
      return;
    }

    try {
      // Create new watchlist with existing properties + new one
      await api().post('/watchlist', {
        name: watchlist.name,
        properties: [...(watchlist.properties || []), selectedProperty]
      });
      // Delete old watchlist
      await api().delete(`/watchlist/${selectedWatchlistId}`);
      toast.success(`Added ${selectedProperty.address} to ${watchlist.name}`);
      setAddToWatchlistDialog(false);
      fetchWatchlists();
    } catch (err) {
      toast.error('Failed to add property');
    }
  };

  // Create new watchlist with property
  const createNewWatchlist = async () => {
    if (!newWatchlistName || !selectedProperty) return;
    
    try {
      await api().post('/watchlist', {
        name: newWatchlistName,
        properties: [selectedProperty]
      });
      toast.success(`Created watchlist "${newWatchlistName}" with ${selectedProperty.address}`);
      setAddToWatchlistDialog(false);
      fetchWatchlists();
    } catch (err) {
      toast.error('Failed to create watchlist');
    }
  };

  // Toggle selection of a result row
  const toggleResultSelection = (index) => {
    setSelectedResults(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      }
      return [...prev, index];
    });
  };

  // Toggle select all results
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedResults([]);
    } else {
      setSelectedResults(violations.map((_, i) => i));
    }
    setSelectAll(!selectAll);
  };

  // Create watchlist from selected results or all results
  const createWatchlistFromResults = async () => {
    if (!quickWatchlistName.trim()) {
      toast.error('Please enter a watchlist name');
      return;
    }

    // Get unique properties from selected results (or all if none selected)
    const indicesToUse = selectedResults.length > 0 ? selectedResults : violations.map((_, i) => i);
    const uniqueProperties = [];
    const seenKeys = new Set();
    
    indicesToUse.forEach(index => {
      const v = violations[index];
      if (!v) return;
      const key = `${v.address}-${v.block}-${v.lot}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        uniqueProperties.push({
          address: v.address,
          block: v.block,
          lot: v.lot,
          datasets: v._dataset ? [v._dataset] : filters.datasets
        });
      }
    });

    if (uniqueProperties.length === 0) {
      toast.error('No properties to add');
      return;
    }

    try {
      await api().post('/watchlist', {
        name: quickWatchlistName.trim(),
        properties: uniqueProperties
      });
      toast.success(`Created watchlist "${quickWatchlistName}" with ${uniqueProperties.length} properties`);
      setShowCreateWatchlistDialog(false);
      setQuickWatchlistName('');
      setSelectedResults([]);
      setSelectAll(false);
      fetchWatchlists();
    } catch (err) {
      toast.error('Failed to create watchlist');
    }
  };

  // Column management functions
  const toggleColumnVisibility = (columnId) => {
    setVisibleColumns(prev => {
      const newState = { ...prev, [columnId]: !prev[columnId] };
      return newState;
    });
    // If making visible and not in order, add to end
    setColumnOrder(prevOrder => {
      if (!visibleColumns[columnId] && !prevOrder.includes(columnId)) {
        const newOrder = [...prevOrder, columnId];
        localStorage.setItem('insite360_column_order', JSON.stringify(newOrder));
        return newOrder;
      }
      return prevOrder;
    });
  };

  const moveColumnUp = (columnId) => {
    setColumnOrder(prev => {
      const index = prev.indexOf(columnId);
      if (index <= 0) return prev;
      const newOrder = [...prev];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      localStorage.setItem('insite360_column_order', JSON.stringify(newOrder));
      return newOrder;
    });
  };

  const moveColumnDown = (columnId) => {
    setColumnOrder(prev => {
      const index = prev.indexOf(columnId);
      if (index === -1 || index >= prev.length - 1) return prev;
      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      localStorage.setItem('insite360_column_order', JSON.stringify(newOrder));
      return newOrder;
    });
  };

  const resetColumnSettings = () => {
    const defaultVisible = {};
    ALL_COLUMNS.forEach(col => {
      defaultVisible[col.id] = col.default;
    });
    setVisibleColumns(defaultVisible);
    setColumnOrder([...DEFAULT_COLUMN_ORDER]);
    localStorage.removeItem('insite360_column_order');
    toast.success('Column settings reset to default');
  };

  // Get ordered visible columns for rendering
  const getOrderedVisibleColumns = () => {
    const seen = new Set();
    const ordered = [];
    
    // First add columns in order that are visible
    columnOrder.forEach(id => {
      if (visibleColumns[id] && !seen.has(id)) {
        seen.add(id);
        ordered.push(id);
      }
    });
    
    // Then add any visible columns not in order (shouldn't happen but just in case)
    Object.keys(visibleColumns).forEach(id => {
      if (visibleColumns[id] && !seen.has(id)) {
        seen.add(id);
        ordered.push(id);
      }
    });
    
    return ordered;
  };

  // Get column label by ID
  const getColumnLabel = (columnId) => {
    const col = ALL_COLUMNS.find(c => c.id === columnId);
    return col?.label || columnId;
  };

  // Render cell content based on column ID
  const renderCell = (violation, columnId) => {
    switch (columnId) {
      case '_dataset':
        return (
          <span className={`text-xs px-2 py-0.5 rounded ${
            violation._dataset === 'dob_violations' ? 'bg-indigo-500/20 text-indigo-300' :
            violation._dataset === 'ecb_violations' ? 'bg-amber-500/20 text-amber-300' :
            violation._dataset === 'fire_violations' ? 'bg-rose-500/20 text-rose-300' :
            violation._dataset === 'dot_violations' ? 'bg-emerald-500/20 text-emerald-300' :
            violation._dataset === 'oath_hearings' ? 'bg-purple-500/20 text-purple-300' :
            violation._dataset === 'oer_sites' ? 'bg-teal-500/20 text-teal-300' :
            violation._dataset === 'dep_environmental' ? 'bg-cyan-500/20 text-cyan-300' :
            violation._dataset === 'e_designations' ? 'bg-lime-500/20 text-lime-300' :
            violation._dataset === 'hpd_violations' ? 'bg-orange-500/20 text-orange-300' :
            violation._dataset === 'bsa_applications' ? 'bg-fuchsia-500/20 text-fuchsia-300' :
            'bg-zinc-500/20 text-zinc-300'
          }`}>
            {DATASET_CONFIG[violation._dataset]?.label?.split(' ')[0] || violation._dataset || '-'}
          </span>
        );
      case 'address':
        // Address displayed as plain text - Property Card available on Watchlist page only
        return (
          <span className="text-sm text-white" data-testid="address-text">
            {violation.address || '-'}
          </span>
        );
      case 'block':
        return <span className="text-sm text-zinc-300">{violation.block || '-'}</span>;
      case 'lot':
        return <span className="text-sm text-zinc-300">{violation.lot || '-'}</span>;
      case 'bin':
        return <span className="text-sm text-zinc-400 font-mono">{violation.bin || '-'}</span>;
      case 'violation_number':
        return <span className="text-sm text-indigo-300 font-mono">{violation.violation_number || '-'}</span>;
      case 'application_number':
        return <span className="text-sm text-cyan-300 font-mono">{violation.application_number || '-'}</span>;
      case 'permit_id':
        return <span className="text-sm text-amber-300 font-mono">{violation.permit_id || '-'}</span>;
      case 'violation_type':
        return <span className="text-xs text-zinc-400 max-w-[150px] truncate" title={violation.violation_type}>{violation.violation_type || '-'}</span>;
      case 'application_type':
        return (
          <span className={`text-xs px-2 py-0.5 rounded ${
            violation.application_type === 'Variance' ? 'bg-purple-500/20 text-purple-300' :
            violation.application_type === 'Special Permit' ? 'bg-blue-500/20 text-blue-300' :
            violation.application_type === 'Appeal' ? 'bg-amber-500/20 text-amber-300' :
            violation.application_type === 'Extension' ? 'bg-teal-500/20 text-teal-300' :
            'bg-zinc-500/20 text-zinc-300'
          }`}>
            {violation.application_type || '-'}
          </span>
        );
      case 'issue_date':
        // Handle both string dates and timestamps
        let dateStr = '-';
        if (violation.issue_date) {
          if (typeof violation.issue_date === 'string') {
            dateStr = violation.issue_date.split('T')[0];
          } else if (typeof violation.issue_date === 'number') {
            dateStr = new Date(violation.issue_date).toLocaleDateString();
          }
        }
        return <span className="text-xs text-zinc-400">{dateStr}</span>;
      case 'status':
        return (
          <span className={`text-xs px-2 py-0.5 rounded ${
            violation.status === 'Open' ? 'bg-rose-500/20 text-rose-400' :
            violation.status === 'Closed' ? 'bg-emerald-500/20 text-emerald-400' :
            'bg-amber-500/20 text-amber-400'
          }`}>
            {violation.status || '-'}
          </span>
        );
      case 'severity':
        return (
          <span className={`text-xs px-2 py-0.5 rounded ${
            violation.severity === 'High' ? 'bg-rose-500/20 text-rose-400' :
            violation.severity === 'Medium' ? 'bg-amber-500/20 text-amber-400' :
            'bg-zinc-500/20 text-zinc-400'
          }`}>
            {violation.severity || '-'}
          </span>
        );
      case 'zoning_district':
        return <span className="text-xs text-zinc-400 font-mono">{violation.zoning_district || '-'}</span>;
      case 'description':
        return <span className="text-xs text-zinc-400 max-w-[200px] truncate" title={violation.description}>{violation.description || '-'}</span>;
      case 'ecb_number':
        return <span className="text-xs text-zinc-400 font-mono">{violation.ecb_number || '-'}</span>;
      case 'penality_amount':
        return <span className="text-xs text-zinc-400">{violation.penality_amount || '-'}</span>;
      case 'decisions_url':
        return violation.decisions_url ? (
          <a href={violation.decisions_url} target="_blank" rel="noopener noreferrer" 
             className="inline-flex items-center gap-1 text-xs text-fuchsia-400 hover:text-fuchsia-300 transition-colors">
            <ExternalLink className="w-3 h-3" /><span>View</span>
          </a>
        ) : <span className="text-xs text-zinc-500">-</span>;
      case 'hearing_date':
        return <span className="text-xs text-zinc-400">{violation.hearing_date ? violation.hearing_date.split('T')[0] : '-'}</span>;
      case 'hearing_result':
        return <span className="text-xs text-zinc-400">{violation.hearing_result || '-'}</span>;
      case 'issuing_agency':
        return <span className="text-xs text-zinc-400">{violation.issuing_agency || '-'}</span>;
      case 'penalty_imposed':
        return <span className="text-xs text-zinc-400">{violation.penalty_imposed || '-'}</span>;
      case 'balance_due':
        return <span className="text-xs text-zinc-400">{violation.balance_due || '-'}</span>;
      case 'apartment':
        return <span className="text-xs text-zinc-400">{violation.apartment || '-'}</span>;
      case 'violation_class':
        return <span className="text-xs text-zinc-400">{violation.violation_class || '-'}</span>;
      case 'rent_impairing':
        return <span className="text-xs text-zinc-400">{violation.rent_impairing || '-'}</span>;
      case 'community_board':
        return <span className="text-xs text-zinc-400">{violation.community_board || '-'}</span>;
      case 'phase':
        return <span className="text-xs text-zinc-400">{violation.phase || '-'}</span>;
      case 'neighborhood':
        return <span className="text-xs text-zinc-400">{violation.neighborhood || '-'}</span>;
      case 'owner':
        return <span className="text-xs text-zinc-400">{violation.owner || '-'}</span>;
      case 'estimated_cost':
        return <span className="text-xs text-zinc-400">{violation.estimated_cost || '-'}</span>;
      case 'zoning':
        return <span className="text-xs text-zinc-400 font-mono">{violation.zoning || '-'}</span>;
      default:
        return <span className="text-xs text-zinc-400">{violation[columnId] || '-'}</span>;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="compliance-page">
        {/* City Selector */}
        <div className="flex items-center gap-4 mb-2 relative z-50">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-zinc-400" />
            <span className="text-sm text-zinc-400">City:</span>
          </div>
          <div className="flex items-center gap-2">
            {Object.entries(CITY_CONFIG).map(([cityId, city]) => (
              <button
                key={cityId}
                onClick={() => handleCityChange(cityId)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedCity === cityId
                    ? 'text-white shadow-lg'
                    : 'text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10'
                }`}
                style={selectedCity === cityId ? { backgroundColor: city.color } : {}}
                data-testid={`city-select-${cityId}`}
              >
                {city.name}
              </button>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Property Search</h1>
            <p className="text-sm text-zinc-500">
              Search {CITY_CONFIG[selectedCity]?.name} data by address, block/lot, or ID
            </p>
          </div>
          <div className="flex items-center gap-2">
            {searchUsage && !searchUsage.searches_unlimited && (
              <div className="flex items-center gap-1.5 px-3 py-1 bg-white/5 border border-white/10 rounded-full" data-testid="search-usage-counter">
                <Search className="w-3 h-3 text-zinc-500" />
                <span className={`text-xs font-medium ${
                  searchUsage.searches_used_today >= searchUsage.searches_per_day 
                    ? 'text-rose-400' 
                    : searchUsage.searches_used_today >= searchUsage.searches_per_day * 0.8 
                      ? 'text-amber-400' 
                      : 'text-zinc-400'
                }`}>
                  {searchUsage.searches_used_today}/{searchUsage.searches_per_day}
                </span>
                <span className="text-[10px] text-zinc-600">today</span>
              </div>
            )}
            <Button 
              onClick={startNewSearch}
              className="btn-primary text-xs h-8"
              data-testid="new-search-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              New Search
            </Button>
            <Button size="sm" className="btn-secondary text-xs h-7" data-testid="export-btn">
              <Download className="w-3 h-3 mr-1" />
              Export
            </Button>
          </div>
        </div>

        {/* Top Center Loading Indicator */}
        {loading && (
          <div className="fixed top-48 left-1/2 -translate-x-1/2 z-50" data-testid="search-loading-indicator">
            <div className="bg-zinc-900/95 backdrop-blur-sm border border-indigo-500/30 rounded-full px-6 py-3 shadow-lg shadow-indigo-500/20">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-5 h-5 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                  <div className="absolute inset-0 w-5 h-5 border-2 border-transparent border-b-cyan-400 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }} />
                </div>
                <span className="text-sm font-medium text-white">Searching {CITY_CONFIG[selectedCity]?.name} databases...</span>
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Saved Searches */}
        {savedSearches.length > 0 && (
          <div className="glass-card p-3 rounded-xl relative z-10">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs text-zinc-400 font-medium">
                <Bookmark className="w-3 h-3" />
                <span>Saved Searches</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {savedSearches.map((search) => (
                <div
                  key={search.id}
                  className="flex items-center gap-1 bg-indigo-500/10 border border-indigo-500/30 rounded-lg overflow-hidden group"
                >
                  <button
                    onClick={() => loadRecentSearch(search)}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-indigo-500/20 transition-colors"
                    data-testid={`saved-search-${search.id}`}
                  >
                    {search.searchMode === 'address' && <MapPin className="w-3 h-3 text-indigo-400" />}
                    {search.searchMode === 'blocklot' && <Building2 className="w-3 h-3 text-emerald-400" />}
                    {search.searchMode === 'bin' && <Hash className="w-3 h-3 text-amber-400" />}
                    <span className="text-xs text-white font-medium">{search.name}</span>
                    <span className="text-[10px] text-zinc-500">{formatRelativeDate(search.date)}</span>
                  </button>
                  <button
                    onClick={() => deleteSavedSearch(search.id)}
                    className="px-2 py-1.5 text-zinc-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors opacity-0 group-hover:opacity-100"
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Searches */}
        {recentSearches.length > 0 && (
          <div className="flex items-center gap-3 relative z-10">
            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
              <History className="w-3 h-3" />
              <span>Recent:</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {recentSearches.map((search) => (
                <button
                  key={search.id}
                  onClick={() => loadRecentSearch(search)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors group"
                  data-testid={`recent-search-${search.id}`}
                >
                  {search.searchMode === 'address' && <MapPin className="w-3 h-3 text-indigo-400" />}
                  {search.searchMode === 'blocklot' && <Building2 className="w-3 h-3 text-emerald-400" />}
                  {search.searchMode === 'bin' && <Hash className="w-3 h-3 text-amber-400" />}
                  <span className="text-xs text-white group-hover:text-indigo-300">{getSearchLabel(search)}</span>
                  <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" />
                    {formatRelativeDate(search.date)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Panel */}
        <div className="glass-card p-4 rounded-xl space-y-4 relative z-20">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-white">New Search</span>
            {(filters.address || filters.block || filters.bin) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSaveDialog(true)}
                className="btn-ghost text-xs h-6"
                data-testid="save-search-btn"
              >
                <Bookmark className="w-3 h-3 mr-1" />
                Save Search
              </Button>
            )}
          </div>
          
          {/* Dataset Selection - Multi-Select (hidden for Application # search) */}
          {searchMode !== 'application' ? (
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label className="text-xs text-zinc-500 mb-1.5 block">Datasets</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between input-dark h-9 font-normal"
                    data-testid="dataset-select"
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Database className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                      <span className="truncate">
                        {filters.datasets.length === 0 
                          ? 'Select datasets...'
                          : filters.datasets.length === 1
                            ? DATASET_CONFIG[filters.datasets[0]]?.label || filters.datasets[0]
                            : `${filters.datasets.length} datasets selected`
                        }
                      </span>
                    </div>
                    <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0 bg-zinc-900 border-white/10" align="start">
                  <div className="p-2 border-b border-white/10">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-zinc-400">Select datasets to search</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (filters.datasets.length === violationDatasets.length) {
                            setFilters(prev => ({ ...prev, datasets: [] }));
                          } else {
                            setFilters(prev => ({ ...prev, datasets: [...violationDatasets] }));
                          }
                        }}
                        className="h-6 px-2 text-xs text-indigo-400 hover:text-indigo-300"
                      >
                        {filters.datasets.length === violationDatasets.length ? 'Clear All' : 'Select All'}
                      </Button>
                    </div>
                  </div>
                  <div className="p-2 space-y-1 max-h-80 overflow-y-auto">
                    {violationDatasets.map((ds) => {
                      const isChecked = filters.datasets.includes(ds);
                      return (
                        <div
                          key={ds}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                          onClick={() => {
                            setFilters(prev => ({
                              ...prev,
                              datasets: isChecked
                                ? prev.datasets.filter(d => d !== ds)
                                : [...prev.datasets, ds]
                            }));
                          }}
                          data-testid={`dataset-option-${ds}`}
                        >
                          <div className={`h-4 w-4 rounded-sm border flex items-center justify-center ${
                            isChecked 
                              ? 'bg-indigo-500 border-indigo-500' 
                              : 'border-zinc-600 bg-transparent'
                          }`}>
                            {isChecked && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <span className="text-sm text-white">{DATASET_CONFIG[ds]?.label || ds}</span>
                        </div>
                      );
                    })}
                  </div>
                  {filters.datasets.length > 0 && (
                    <div className="p-2 border-t border-white/10">
                      <div className="flex flex-wrap gap-1">
                        {filters.datasets.map(ds => (
                          <span 
                            key={ds}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-500/20 text-indigo-300 rounded text-xs"
                          >
                            {DATASET_CONFIG[ds]?.label?.substring(0, 10) || ds}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setFilters(prev => ({
                                  ...prev,
                                  datasets: prev.datasets.filter(d => d !== ds)
                                }));
                              }}
                              className="hover:text-white"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </div>
          ) : (
            /* Application # search - show info instead of dataset selector */
            <div className="flex items-center gap-3 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
              <FileText className="w-5 h-5 text-indigo-400" />
              <div>
                <p className="text-sm text-white font-medium">Application Number Search</p>
                <p className="text-xs text-zinc-400">Searches across DOB BIS Applications and DOB NOW Build filings</p>
              </div>
            </div>
          )}

          {/* Search Mode Tabs */}
          <div>
            <Label className="text-xs text-zinc-500 mb-1.5 block">Search By</Label>
            <div className="flex gap-1 p-1 bg-white/5 rounded-lg w-fit">
              {SEARCH_MODES.map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setSearchMode(mode.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    searchMode === mode.id 
                      ? 'bg-indigo-500 text-white' 
                      : 'text-zinc-400 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid={`search-mode-${mode.id}`}
                >
                  <mode.icon className="w-3 h-3" />
                  {mode.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search Inputs based on mode */}
          <div className="flex items-end gap-3">
            {searchMode === 'address' && (
              <>
                <div className="flex-1 relative z-[60]">
                  <Label className="text-xs text-zinc-500 mb-1.5 block">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      ref={addressInputRef}
                      placeholder={selectedCity === 'dc' ? "Start typing a DC address..." : "Start typing an NYC address..."}
                      value={filters.address}
                      onChange={(e) => handleAddressChange(e.target.value)}
                      onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                      className="input-dark h-9 pl-10 pr-8"
                      data-testid="address-input"
                      autoComplete="off"
                    />
                    {loadingSuggestions && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-400 animate-spin" />
                    )}
                  </div>
                  
                  {/* Address Suggestions Dropdown */}
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <div 
                      ref={suggestionsRef}
                      className="absolute z-[100] w-full mt-1 bg-zinc-900 border border-white/10 rounded-lg shadow-xl max-h-80 overflow-y-auto"
                    >
                      {addressSuggestions.map((suggestion, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => selectAddress(suggestion)}
                          className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-start gap-3 border-b border-white/5 last:border-0"
                        >
                          <MapPin className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{suggestion.address}</p>
                            <p className="text-xs text-zinc-500">
                              {suggestion.ssl ? (
                                <>SSL: {suggestion.ssl}{suggestion.ward && ` • Ward ${suggestion.ward}`}</>
                              ) : (
                                <>Block {suggestion.block}, Lot {suggestion.lot}{suggestion.borough && ` • ${suggestion.borough}`}</>
                              )}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Borough Selector - Right next to address for NYC */}
                {selectedCity === 'nyc' && (
                  <div className="w-36">
                    <Label className="text-xs text-zinc-500 mb-1.5 block">Borough</Label>
                    <Select 
                      value={filters.borough} 
                      onValueChange={(v) => setFilters(prev => ({ ...prev, borough: v }))}
                    >
                      <SelectTrigger className="input-dark h-9" data-testid="borough-selector">
                        <SelectValue placeholder="All Boroughs" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Boroughs</SelectItem>
                        <SelectItem value="Manhattan">Manhattan</SelectItem>
                        <SelectItem value="Brooklyn">Brooklyn</SelectItem>
                        <SelectItem value="Queens">Queens</SelectItem>
                        <SelectItem value="Bronx">Bronx</SelectItem>
                        <SelectItem value="Staten Island">Staten Island</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {searchMode === 'blocklot' && (
              <>
                <div className="w-32">
                  <Label className="text-xs text-zinc-500 mb-1.5 block">Block</Label>
                  <Input
                    placeholder="e.g. 1305"
                    value={filters.block}
                    onChange={(e) => setFilters(prev => ({ ...prev, block: e.target.value }))}
                    className="input-dark h-9"
                    data-testid="block-input"
                  />
                </div>
                <div className="w-32">
                  <Label className="text-xs text-zinc-500 mb-1.5 block">Lot</Label>
                  <Input
                    placeholder="e.g. 1"
                    value={filters.lot}
                    onChange={(e) => setFilters(prev => ({ ...prev, lot: e.target.value }))}
                    className="input-dark h-9"
                    data-testid="lot-input"
                  />
                </div>
              </>
            )}

            {searchMode === 'bin' && (
              <div className="w-48">
                <Label className="text-xs text-zinc-500 mb-1.5 block">BIN (Building ID Number)</Label>
                <Input
                  placeholder="e.g. 1001831"
                  value={filters.bin}
                  onChange={(e) => setFilters(prev => ({ ...prev, bin: e.target.value }))}
                  className="input-dark h-9"
                  data-testid="bin-input"
                />
              </div>
            )}

            {searchMode === 'application' && (
              <div className="flex-1 max-w-md">
                <Label className="text-xs text-zinc-500 mb-1.5 block">Application / Job Number</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    placeholder="e.g. 123456789 or B00123456"
                    value={filters.applicationId}
                    onChange={(e) => setFilters(prev => ({ ...prev, applicationId: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="input-dark h-9 pl-10"
                    data-testid="application-id-input"
                  />
                </div>
              </div>
            )}

            <Button 
              onClick={handleSearch} 
              className="btn-primary h-9 px-6"
              disabled={loading}
              data-testid="search-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </Button>

            {(filters.address || filters.block || filters.lot || filters.bin || filters.applicationId) && (
              <Button 
                type="button" 
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters(prev => ({ ...prev, address: '', block: '', lot: '', bin: '', applicationId: '' }));
                  setViolations([]);
                  setTotal(0);
                }}
                className="btn-ghost h-9"
              >
                Clear
              </Button>
            )}
          </div>
          
          {/* Block/Lot auto-detection removed - searches now use address only */}
        </div>

        {/* Column Settings Dialog - Enhanced */}
        <Dialog open={showColumnSettings} onOpenChange={setShowColumnSettings}>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center justify-between">
                <span>Manage Columns</span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={resetColumnSettings}
                  className="text-xs text-zinc-400 hover:text-white"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Reset
                </Button>
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-hidden flex flex-col">
              <p className="text-xs text-zinc-500 mb-3">
                Toggle columns on/off and drag to reorder. Changes are saved automatically.
              </p>
              
              {/* Active Columns Section */}
              <div className="mb-4">
                <p className="text-xs font-medium text-indigo-400 mb-2 flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  Visible Columns ({columnOrder.filter(id => visibleColumns[id]).length})
                </p>
                <div className="space-y-1 max-h-[200px] overflow-y-auto pr-2">
                  {columnOrder.filter(id => visibleColumns[id]).map((columnId, index) => (
                    <div 
                      key={columnId}
                      className="flex items-center gap-2 p-2 bg-white/5 rounded-lg hover:bg-white/10 group"
                    >
                      <GripVertical className="w-4 h-4 text-zinc-600 cursor-grab" />
                      <span className="flex-1 text-sm text-white">{getColumnLabel(columnId)}</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveColumnUp(columnId)}
                          disabled={index === 0}
                          className="h-6 w-6 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveColumnDown(columnId)}
                          disabled={index === columnOrder.filter(id => visibleColumns[id]).length - 1}
                          className="h-6 w-6 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </Button>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleColumnVisibility(columnId)}
                        className="h-6 w-6 p-0 text-zinc-400 hover:text-rose-400"
                        title="Hide column"
                      >
                        <EyeOff className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hidden Columns Section */}
              <div>
                <p className="text-xs font-medium text-zinc-500 mb-2 flex items-center gap-1">
                  <EyeOff className="w-3 h-3" />
                  Hidden Columns ({ALL_COLUMNS.filter(c => !visibleColumns[c.id]).length})
                </p>
                <div className="grid grid-cols-2 gap-1 max-h-[150px] overflow-y-auto pr-2">
                  {ALL_COLUMNS.filter(col => !visibleColumns[col.id]).map(col => (
                    <button
                      key={col.id}
                      onClick={() => toggleColumnVisibility(col.id)}
                      className="flex items-center gap-2 p-2 bg-white/5 rounded-lg hover:bg-indigo-500/20 hover:border-indigo-500/30 border border-transparent transition-all text-left"
                      data-testid={`add-column-${col.id}`}
                    >
                      <Plus className="w-3 h-3 text-zinc-500" />
                      <span className="text-xs text-zinc-400 hover:text-white truncate">{col.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-white/10 mt-4">
              <Button variant="ghost" onClick={() => setShowColumnSettings(false)}>
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Save Search Dialog */}
        <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white">Save Search</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Search Name</Label>
                <Input
                  placeholder="e.g. RFR Properties, Manhattan Block 1305..."
                  value={saveSearchName}
                  onChange={(e) => setSaveSearchName(e.target.value)}
                  className="input-dark"
                  autoFocus
                  data-testid="save-search-name-input"
                />
              </div>
              <div className="text-xs text-zinc-500 bg-white/5 rounded-lg p-3">
                <p className="font-medium text-zinc-400 mb-1">Search criteria:</p>
                <p>
                  {searchMode === 'address' && filters.address && `Address: ${filters.address}`}
                  {searchMode === 'blocklot' && `Block ${filters.block}, Lot ${filters.lot}`}
                  {searchMode === 'bin' && `BIN: ${filters.bin}`}
                </p>
                <p>Datasets: {filters.datasets.map(ds => DATASET_CONFIG[ds]?.label || ds).join(', ')}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowSaveDialog(false)}>
                Cancel
              </Button>
              <Button onClick={saveSearchWithName} className="btn-primary" data-testid="confirm-save-search-btn">
                <Bookmark className="w-3 h-3 mr-1" />
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Results Section */}
        {violations.length > 0 ? (
          <div className="glass-card rounded-xl overflow-hidden relative z-10">
            {/* Associated Addresses Alert */}
            {(() => {
              // Extract unique addresses from results
              const uniqueAddresses = [...new Set(violations.map(v => v.address).filter(Boolean))];
              const uniqueBlocks = [...new Set(violations.map(v => v.block).filter(Boolean))];
              
              // Show alert if multiple addresses found for same block or if more than one unique address
              if (uniqueAddresses.length > 1 && uniqueBlocks.length <= 2) {
                return (
                  <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/30 flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-amber-400">Multiple Associated Addresses Found</div>
                      <div className="text-xs text-zinc-400 mt-1">
                        This property has records under {uniqueAddresses.length} different addresses:
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {uniqueAddresses.slice(0, 5).map((addr, idx) => (
                          <span key={idx} className="text-xs bg-white/10 text-white px-2 py-1 rounded">
                            {addr}
                          </span>
                        ))}
                        {uniqueAddresses.length > 5 && (
                          <span className="text-xs text-zinc-500">+{uniqueAddresses.length - 5} more</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-zinc-900/50">
              <div className="flex items-center gap-3">
                <span className="text-sm text-zinc-400">
                  {total.toLocaleString()} records found
                  {filters.datasets.length > 1 && ` across ${filters.datasets.length} datasets`}
                </span>
                {selectedResults.length > 0 && (
                  <span className="text-xs text-indigo-400 bg-indigo-500/20 px-2 py-0.5 rounded">
                    {selectedResults.length} selected
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {/* Columns Button */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowColumnSettings(true)}
                  className="btn-ghost text-xs h-7"
                  data-testid="columns-btn"
                >
                  <Settings2 className="w-3 h-3 mr-1" />
                  Columns
                </Button>
                
                {/* Create Watchlist Button */}
                <Button 
                  onClick={() => setShowCreateWatchlistDialog(true)}
                  className="btn-primary h-7 text-xs"
                  data-testid="create-watchlist-from-results-btn"
                >
                  <FolderPlus className="w-3 h-3 mr-1" />
                  Create Watchlist
                </Button>
                
                {/* Status/Severity Filters */}
                <Select 
                  value={filters.status} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger className="input-dark h-7 text-xs w-28">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select 
                  value={filters.severity} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, severity: v }))}
                >
                  <SelectTrigger className="input-dark h-7 text-xs w-28">
                    <SelectValue placeholder="Severity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Severity</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Dynamic Table */}
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="text-xs w-10">
                      <Checkbox
                        checked={selectAll}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    {getOrderedVisibleColumns().map(columnId => (
                      <th key={columnId} className="text-xs">{getColumnLabel(columnId)}</th>
                    ))}
                    <th className="text-xs w-16 text-right pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {violations.map((v, i) => (
                    <tr 
                      key={i} 
                      className={`hover:bg-white/5 cursor-pointer transition-colors ${selectedResults.includes(i) ? 'bg-indigo-500/10' : ''}`}
                      onClick={() => openPropertyCard(v)}
                      title="Click to view Property Card"
                      data-testid={`result-row-${i}`}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedResults.includes(i)}
                          onCheckedChange={() => toggleResultSelection(i)}
                          data-testid={`select-row-${i}`}
                        />
                      </td>
                      {getOrderedVisibleColumns().map(columnId => (
                        <td key={columnId}>{renderCell(v, columnId)}</td>
                      ))}
                      <td onClick={(e) => e.stopPropagation()} className="text-right pr-4">
                        <button
                          onClick={() => openAddToWatchlist(v)}
                          className="inline-flex items-center justify-center h-8 w-8 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/20 border border-indigo-500/30 rounded"
                          title="Add to Watchlist"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <span className="text-xs text-zinc-500">
                Page {page + 1} of {totalPages || 1}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="h-7"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="h-7"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="glass-card rounded-xl p-12 text-center">
            <Search className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Search for Building Data</h3>
            <p className="text-sm text-zinc-500 max-w-md mx-auto">
              Select a dataset and search by address, block/lot, or BIN to find building records.
            </p>
          </div>
        )}

        {/* Add to Watchlist Dialog */}
        <Dialog open={addToWatchlistDialog} onOpenChange={setAddToWatchlistDialog}>
          <DialogContent className="glass-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Add to Watchlist</DialogTitle>
            </DialogHeader>
            
            {selectedProperty && (
              <div className="mt-4 space-y-4">
                <div className="bg-white/5 p-4 rounded-lg">
                  <p className="text-sm text-zinc-400 mb-1">Property</p>
                  <p className="text-white font-medium">{selectedProperty.address}</p>
                  <p className="text-sm text-zinc-400">Block {selectedProperty.block}, Lot {selectedProperty.lot}</p>
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <p className="text-xs text-zinc-500 mb-1">Will monitor dataset(s):</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedProperty.datasets?.map(ds => (
                        <span key={ds} className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded">
                          {DATASET_CONFIG[ds]?.label || ds}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {watchlists.length > 0 && (
                  <div>
                    <p className="text-sm text-zinc-300 mb-2">Add to existing watchlist</p>
                    <div className="space-y-2">
                      {watchlists.map(wl => (
                        <button
                          key={wl.id}
                          onClick={() => setSelectedWatchlistId(wl.id)}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            selectedWatchlistId === wl.id 
                              ? 'border-indigo-500 bg-indigo-500/10' 
                              : 'border-white/10 hover:border-white/20 bg-white/5'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-white">{wl.name}</span>
                            {selectedWatchlistId === wl.id && (
                              <Check className="w-4 h-4 text-indigo-400" />
                            )}
                          </div>
                          <span className="text-xs text-zinc-500">{wl.properties?.length || 0} properties</span>
                        </button>
                      ))}
                    </div>
                    <Button 
                      onClick={addToExistingWatchlist}
                      disabled={!selectedWatchlistId}
                      className="w-full btn-primary mt-3"
                      data-testid="add-to-existing-btn"
                    >
                      Add to Selected Watchlist
                    </Button>
                  </div>
                )}

                <div className="border-t border-white/10 pt-4">
                  <p className="text-sm text-zinc-300 mb-2">Or create new watchlist</p>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Watchlist name"
                      value={newWatchlistName}
                      onChange={(e) => setNewWatchlistName(e.target.value)}
                      className="input-dark flex-1"
                      data-testid="new-watchlist-name"
                    />
                    <Button 
                      onClick={createNewWatchlist}
                      disabled={!newWatchlistName}
                      className="btn-secondary"
                      data-testid="create-new-watchlist-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Watchlist from Results Dialog */}
        <Dialog open={showCreateWatchlistDialog} onOpenChange={setShowCreateWatchlistDialog}>
          <DialogContent className="glass-card border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Create Watchlist from Results</DialogTitle>
            </DialogHeader>
            
            <div className="mt-4 space-y-4">
              <div className="bg-white/5 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-zinc-400">Properties to add</p>
                  <span className="text-xs text-indigo-400 bg-indigo-500/20 px-2 py-1 rounded">
                    {selectedResults.length > 0 
                      ? `${selectedResults.length} selected` 
                      : `${violations.length} total (all)`
                    }
                  </span>
                </div>
                <p className="text-xs text-zinc-500">
                  {selectedResults.length > 0 
                    ? 'Selected properties from your search results will be added'
                    : 'All unique properties from current page will be added'
                  }
                </p>
                {selectedResults.length === 0 && (
                  <p className="text-xs text-amber-400 mt-2">
                    Tip: Use checkboxes to select specific properties
                  </p>
                )}
              </div>

              <div>
                <Label className="text-xs text-zinc-500 mb-1.5 block">Watchlist Name</Label>
                <Input
                  placeholder="e.g. Manhattan Portfolio, East Village Properties..."
                  value={quickWatchlistName}
                  onChange={(e) => setQuickWatchlistName(e.target.value)}
                  className="input-dark"
                  autoFocus
                  data-testid="quick-watchlist-name-input"
                  onKeyDown={(e) => e.key === 'Enter' && createWatchlistFromResults()}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setShowCreateWatchlistDialog(false);
                    setQuickWatchlistName('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={createWatchlistFromResults}
                  disabled={!quickWatchlistName.trim()}
                  className="btn-primary"
                  data-testid="confirm-create-watchlist-btn"
                >
                  <FolderPlus className="w-4 h-4 mr-2" />
                  Create Watchlist
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Property Card Modal */}
        <Dialog open={propertyCardOpen} onOpenChange={setPropertyCardOpen}>
          <DialogContent className="glass-card border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-lg">
                  <Building2 className="w-5 h-5 text-amber-400" />
                </div>
                Property Card
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {propertyCardData?.address || 'Loading property details...'}
              </DialogDescription>
            </DialogHeader>
            
            {propertyCardLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
              </div>
            ) : propertyCardData ? (
              <div className="space-y-6 py-4">
                {/* Prominent Risk Score */}
                <div className="flex items-center justify-center">
                  <div className={`text-center p-6 rounded-xl ${
                    propertyCardData.riskScore?.risk_level === 'HIGH' ? 'bg-rose-500/10 border border-rose-500/30' :
                    propertyCardData.riskScore?.risk_level === 'MODERATE' ? 'bg-amber-500/10 border border-amber-500/30' :
                    'bg-emerald-500/10 border border-emerald-500/30'
                  }`}>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Shield className={`w-6 h-6 ${
                        propertyCardData.riskScore?.risk_level === 'HIGH' ? 'text-rose-400' :
                        propertyCardData.riskScore?.risk_level === 'MODERATE' ? 'text-amber-400' :
                        'text-emerald-400'
                      }`} />
                      <span className="text-sm font-medium text-zinc-400">INSITE RISK SCORE</span>
                    </div>
                    <div className={`text-5xl font-bold ${
                      propertyCardData.riskScore?.risk_level === 'HIGH' ? 'text-rose-400' :
                      propertyCardData.riskScore?.risk_level === 'MODERATE' ? 'text-amber-400' :
                      'text-emerald-400'
                    }`}>
                      {propertyCardData.riskScore?.score || 0}
                    </div>
                    <div className={`text-sm font-medium mt-1 ${
                      propertyCardData.riskScore?.risk_level === 'HIGH' ? 'text-rose-400' :
                      propertyCardData.riskScore?.risk_level === 'MODERATE' ? 'text-amber-400' :
                      'text-emerald-400'
                    }`}>
                      {propertyCardData.riskScore?.risk_level || 'LOW'} RISK
                    </div>
                  </div>
                </div>

                {/* Property Identifiers */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <div className="text-xs text-zinc-500 mb-1">{propertyCardData.isDC ? 'SQUARE' : 'BLOCK'}</div>
                    <div className="text-lg font-bold text-white">{propertyCardData.block || propertyCardData.ssl?.split(' ')[0] || 'N/A'}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <div className="text-xs text-zinc-500 mb-1">{propertyCardData.isDC ? 'LOT/SUFFIX' : 'LOT'}</div>
                    <div className="text-lg font-bold text-white">{propertyCardData.lot || 'N/A'}</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-4 text-center">
                    <div className="text-xs text-zinc-500 mb-1">{propertyCardData.isDC ? 'SSL' : 'BIN'}</div>
                    <div className="text-lg font-bold text-white">{propertyCardData.isDC ? propertyCardData.ssl : propertyCardData.bin || 'N/A'}</div>
                  </div>
                </div>

                {/* Status Indicators - NYC Only */}
                {!propertyCardData.isDC && (
                <div className="grid grid-cols-2 gap-4">
                  {/* Landmark Status */}
                  <div className={`rounded-lg p-4 flex items-center gap-3 ${
                    propertyCardData.landmark?.is_landmark ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-white/5'
                  }`}>
                    <Landmark className={`w-5 h-5 ${propertyCardData.landmark?.is_landmark ? 'text-purple-400' : 'text-zinc-500'}`} />
                    <div>
                      <div className="text-xs text-zinc-500">LANDMARK STATUS</div>
                      <div className={`font-medium ${propertyCardData.landmark?.is_landmark ? 'text-purple-400' : 'text-zinc-400'}`}>
                        {propertyCardData.landmark?.is_landmark ? 'Designated Landmark' : 'Not Landmarked'}
                      </div>
                      {propertyCardData.landmark?.landmark_name && (
                        <div className="text-xs text-purple-300 mt-0.5">{propertyCardData.landmark.landmark_name}</div>
                      )}
                    </div>
                  </div>

                  {/* Flood Zone */}
                  <div className={`rounded-lg p-4 flex items-center gap-3 ${
                    propertyCardData.floodZone?.in_flood_zone ? 'bg-sky-500/10 border border-sky-500/30' : 'bg-white/5'
                  }`}>
                    <Droplets className={`w-5 h-5 ${propertyCardData.floodZone?.in_flood_zone ? 'text-sky-400' : 'text-zinc-500'}`} />
                    <div>
                      <div className="text-xs text-zinc-500">FLOOD ZONE</div>
                      <div className={`font-medium ${propertyCardData.floodZone?.in_flood_zone ? 'text-sky-400' : 'text-zinc-400'}`}>
                        {propertyCardData.floodZone?.flood_zone || 'Zone X (Minimal)'}
                      </div>
                      {propertyCardData.floodZone?.in_flood_zone && (
                        <div className="text-xs text-sky-300 mt-0.5">Special Flood Hazard Area</div>
                      )}
                    </div>
                  </div>

                  {/* OER Environmental */}
                  <div className={`rounded-lg p-4 flex items-center gap-3 ${
                    propertyCardData.oer?.has_oer_record ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-white/5'
                  }`}>
                    <Leaf className={`w-5 h-5 ${propertyCardData.oer?.has_oer_record ? 'text-amber-400' : 'text-zinc-500'}`} />
                    <div>
                      <div className="text-xs text-zinc-500">OER STATUS</div>
                      <div className={`font-medium ${propertyCardData.oer?.has_oer_record ? 'text-amber-400' : 'text-zinc-400'}`}>
                        {propertyCardData.oer?.has_oer_record ? 'Environmental Record' : 'No OER Record'}
                      </div>
                      {propertyCardData.oer?.status && (
                        <div className="text-xs text-amber-300 mt-0.5">{propertyCardData.oer.status}</div>
                      )}
                    </div>
                  </div>

                  {/* Violations Summary */}
                  <div className="bg-white/5 rounded-lg p-4 flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-rose-400" />
                    <div>
                      <div className="text-xs text-zinc-500">VIOLATIONS</div>
                      <div className="flex items-center gap-3">
                        <span className="text-rose-400 font-medium">{propertyCardData.openViolations || 0} Open</span>
                        <span className="text-zinc-500">|</span>
                        <span className="text-emerald-400 font-medium">{propertyCardData.closedViolations || 0} Closed</span>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Open Applications */}
                <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-indigo-400" />
                    <div>
                      <div className="text-xs text-zinc-500">OPEN APPLICATIONS</div>
                      <div className="text-indigo-400 font-medium">{propertyCardData.openApplications || 0} Active Permits/Applications</div>
                    </div>
                  </div>
                </div>

                {/* Owner Summary (PLUTO/CAMA Data) */}
                {propertyCardData.pluto?.ownerName && (
                  <div className="bg-gradient-to-r from-indigo-500/10 to-purple-500/10 border border-indigo-500/30 rounded-lg p-4">
                    <div className="text-xs text-zinc-500 mb-3 flex items-center gap-2">
                      <Home className="w-3 h-3" />
                      PROPERTY OWNER
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10">{propertyCardData.isDC ? 'DC CAMA' : 'NYC PLUTO'}</span>
                    </div>
                    <div className="space-y-3">
                      {/* Owner Name */}
                      <div>
                        <div className="text-xs text-zinc-500 mb-1">Registered Owner</div>
                        <div className="text-white font-semibold text-lg">
                          {propertyCardData.pluto.ownerName}
                        </div>
                      </div>
                      
                      {/* Assessed Value */}
                      {propertyCardData.pluto.assessedValue && (
                        <div className="flex items-center justify-between bg-emerald-500/10 rounded p-3 mt-2">
                          <span className="text-zinc-400">{propertyCardData.isDC ? 'DC' : 'NYC'} Assessed Value:</span>
                          <span className="text-emerald-400 font-bold text-xl">{propertyCardData.pluto.assessedValue}</span>
                        </div>
                      )}
                      
                      {/* Additional PLUTO Details */}
                      <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t border-white/10">
                        {propertyCardData.pluto.assessedLand && (
                          <div>
                            <span className="text-zinc-500">Land Value:</span>
                            <span className="text-white ml-2">{propertyCardData.pluto.assessedLand}</span>
                          </div>
                        )}
                        {propertyCardData.pluto.fiscalYear && (
                          <div>
                            <span className="text-zinc-500">Fiscal Year:</span>
                            <span className="text-white ml-2">{propertyCardData.pluto.fiscalYear}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Certificates of Occupancy */}
                {propertyCardData.certificatesOfOccupancy && (
                  <div className={`rounded-lg p-4 ${
                    propertyCardData.certificatesOfOccupancy.total_count > 0 
                      ? 'bg-emerald-500/10 border border-emerald-500/30' 
                      : 'bg-white/5'
                  }`}>
                    <div className="text-xs text-zinc-500 mb-3 flex items-center gap-2">
                      <Award className="w-3 h-3" />
                      CERTIFICATES OF OCCUPANCY
                    </div>
                    {propertyCardData.certificatesOfOccupancy.total_count > 0 ? (
                      <div className="space-y-3">
                        {/* Summary counts */}
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-emerald-400">
                              {propertyCardData.certificatesOfOccupancy.total_count}
                            </div>
                            <div className="text-xs text-zinc-500">Total C of O</div>
                          </div>
                          <div className="h-10 w-px bg-white/10"></div>
                          <div className="flex gap-4">
                            <div>
                              <span className="text-emerald-400 font-medium">{propertyCardData.certificatesOfOccupancy.final_count}</span>
                              <span className="text-zinc-500 text-sm ml-1">Final</span>
                            </div>
                            <div>
                              <span className="text-amber-400 font-medium">{propertyCardData.certificatesOfOccupancy.temporary_count}</span>
                              <span className="text-zinc-500 text-sm ml-1">Temporary</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Most recent */}
                        {propertyCardData.certificatesOfOccupancy.most_recent && (
                          <div className="pt-2 border-t border-white/10">
                            <div className="text-xs text-zinc-500 mb-1">Most Recent Certificate</div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-zinc-500">Issued:</span>
                                <span className="text-white ml-2">
                                  {new Date(propertyCardData.certificatesOfOccupancy.most_recent.issue_date).toLocaleDateString()}
                                </span>
                              </div>
                              <div>
                                <span className="text-zinc-500">Type:</span>
                                <span className={`ml-2 ${propertyCardData.certificatesOfOccupancy.most_recent.issue_type === 'Final' ? 'text-emerald-400' : 'text-amber-400'}`}>
                                  {propertyCardData.certificatesOfOccupancy.most_recent.issue_type}
                                </span>
                              </div>
                              <div>
                                <span className="text-zinc-500">Job #:</span>
                                <span className="text-white ml-2">{propertyCardData.certificatesOfOccupancy.most_recent.job_number}</span>
                              </div>
                              <div>
                                <span className="text-zinc-500">Status:</span>
                                <span className="text-white ml-2">{propertyCardData.certificatesOfOccupancy.most_recent.status}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-zinc-500 text-sm">No certificates of occupancy on file</div>
                    )}
                  </div>
                )}

                {/* Building Info from PLUTO (if available) */}
                {propertyCardData.pluto && (
                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-xs text-zinc-500 mb-3 flex items-center gap-2">
                      <Info className="w-3 h-3" />
                      BUILDING INFO
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {propertyCardData.pluto.year_built && (
                        <div>
                          <span className="text-zinc-500">Year Built:</span>
                          <span className="text-white ml-2">{propertyCardData.pluto.year_built}</span>
                        </div>
                      )}
                      {propertyCardData.pluto.building_class && (
                        <div>
                          <span className="text-zinc-500">Building Class:</span>
                          <span className="text-white ml-2">{propertyCardData.pluto.building_class}</span>
                        </div>
                      )}
                      {propertyCardData.pluto.zoning && (
                        <div>
                          <span className="text-zinc-500">Zoning:</span>
                          <span className="text-white ml-2">{propertyCardData.pluto.zoning}</span>
                        </div>
                      )}
                      {propertyCardData.pluto.num_floors && (
                        <div>
                          <span className="text-zinc-500">Floors:</span>
                          <span className="text-white ml-2">{propertyCardData.pluto.num_floors}</span>
                        </div>
                      )}
                      {propertyCardData.pluto.units_total && (
                        <div>
                          <span className="text-zinc-500">Units:</span>
                          <span className="text-white ml-2">{propertyCardData.pluto.units_total}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="space-y-3 pt-2">
                  {/* Enhanced Report Options (Enterprise Only) - NYC only */}
                  {!propertyCardData.isDC && (
                    <>
                      {isEnterpriseTier ? (
                        <div className="space-y-2">
                          <p className="text-xs text-zinc-400 mb-2">Generate Compliance Report:</p>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => handleGenerateEnhancedReport('executive')}
                              disabled={generatingEnhancedReport || !propertyCardData?.address}
                              className="p-2 rounded-lg border border-teal-500/30 bg-teal-500/10 hover:bg-teal-500/20 transition-colors text-left"
                              data-testid="executive-report-btn"
                            >
                              <div className="flex items-center gap-1 text-teal-400 font-medium text-xs">
                                <FileBarChart className="w-3 h-3" />
                                Executive
                              </div>
                              <p className="text-[10px] text-zinc-500 mt-1">
                                Summary + AI Insite
                              </p>
                            </button>
                            <button
                              onClick={() => handleGenerateEnhancedReport('extended')}
                              disabled={generatingEnhancedReport || !propertyCardData?.address}
                              className="p-2 rounded-lg border border-purple-500/30 bg-purple-500/10 hover:bg-purple-500/20 transition-colors text-left"
                              data-testid="extended-report-btn"
                            >
                              <div className="flex items-center gap-1 text-purple-400 font-medium text-xs">
                                <FileStack className="w-3 h-3" />
                                Extended
                              </div>
                              <p className="text-[10px] text-zinc-500 mt-1">
                                + Open violations & applications
                              </p>
                            </button>
                          </div>
                          {generatingEnhancedReport && (
                            <div className="flex items-center justify-center gap-2 py-2 text-amber-400 text-sm">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generating report...
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <Button
                            disabled
                            className="w-full bg-zinc-800 border border-zinc-700 text-zinc-500 cursor-not-allowed"
                          >
                            <Crown className="w-4 h-4 mr-2" />
                            Enhanced Report (Enterprise)
                          </Button>
                          <p className="text-xs text-center text-zinc-500">
                            Upgrade to Enterprise tier to access enhanced compliance reports
                          </p>
                        </>
                      )}
                    </>
                  )}
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={() => {
                        setPropertyCardOpen(false);
                        openAddToWatchlist({
                          address: propertyCardData.address,
                          block: propertyCardData.block,
                          lot: propertyCardData.lot,
                          bin: propertyCardData.bin
                        });
                      }}
                      className="btn-primary flex-1"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add to Watchlist
                    </Button>
                    {propertyCardData.isDC ? (
                      <Button
                        variant="outline"
                        onClick={() => window.open(`https://opendata.dc.gov/search?q=${encodeURIComponent(propertyCardData.ssl || propertyCardData.address)}`, '_blank')}
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        DC Property Lookup
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => window.open(`https://a810-bisweb.nyc.gov/bisweb/PropertyProfileOverviewServlet?boro=3&block=${propertyCardData.block}&lot=${propertyCardData.lot}`, '_blank')}
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        NYC DOB Profile
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                No property data available
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* API Connectivity Error Dialog */}
        <Dialog open={apiErrorDialog.open} onOpenChange={(open) => setApiErrorDialog(prev => ({ ...prev, open }))}>
          <DialogContent className="bg-zinc-900 border-amber-500/30 max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-full bg-amber-500/20">
                  <AlertTriangle className="w-6 h-6 text-amber-400" />
                </div>
                <DialogTitle className="text-amber-400 text-lg">{apiErrorDialog.message}</DialogTitle>
              </div>
              <DialogDescription className="text-zinc-300 text-sm leading-relaxed">
                {apiErrorDialog.details}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-300">
                  <strong>What you can do:</strong>
                  <ul className="mt-1 space-y-1 list-disc list-inside text-amber-200/80">
                    <li>Wait a few minutes and try again</li>
                    <li>Try searching with fewer datasets</li>
                    <li>Check the NYC/DC Open Data status page</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setApiErrorDialog(prev => ({ ...prev, open: false }))}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Dismiss
              </Button>
              <Button
                onClick={() => {
                  setApiErrorDialog(prev => ({ ...prev, open: false }));
                  fetchViolations();
                }}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Try Again
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
